import React, {useEffect, useRef, useState} from 'react';
import {MicroreelVideoSource} from '../microreelVideoSources';
import {microreelsListText} from '../microreelsListText';
import styles from './MicroreelVideoSurface.module.css';

// The native controls are hidden on purpose: Safari's overlay (PiP,
// fullscreen, floating volume bar) clutters the small phone frame and its
// unmute affordance is unreliable for a video that had to start muted
// (autoplay policy). The reel keeps its own minimal controls instead:
// tap to pause/resume and a "Tap for sound" pill — the tap is a user
// gesture, which makes unmuting legal mid-playback.

// `timeupdate` only fires about four times a second, so ending a segment
// there overran endInSeconds by up to 250ms — enough to play the head of the
// next reel and then play it again once that reel loaded. The end is watched
// per presented frame instead, which lands within one frame of the cut.
type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    callback: (now: number, metadata: {mediaTime: number}) => void,
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

// Below this the element is already at the wanted position and seeking would
// only add a stutter — consecutive segments of one file meet exactly here.
const SEEK_TOLERANCE_SECONDS = 0.02;

const startSegment = (
  video: HTMLVideoElement,
  startInSeconds: number,
  muted: boolean,
) => {
  video.muted = muted;
  if (Math.abs(video.currentTime - startInSeconds) > SEEK_TOLERANCE_SECONDS) {
    video.currentTime = startInSeconds;
  }
  video.play().catch((error: unknown) => {
    // A new element may not start with sound outside a tap (Safari), so it
    // falls back to the muted start rather than a stalled frame.
    if (
      (error as Error | undefined)?.name === 'NotAllowedError' &&
      !video.muted
    ) {
      video.muted = true;
      video.play().catch(() => undefined);
    }
  });
};

// Blocked autoplay or a stalled seek would otherwise leave the frame blank.
const REVEAL_TIMEOUT_MS = 1200;

// Resolves when a frame of the new segment is on screen, so the fade in never
// uncovers the previous one.
const revealOnNextFrame = (video: FrameCallbackVideo, reveal: () => void) => {
  let frameHandle = 0;
  let rafHandle = 0;
  let done = false;

  const finish = () => {
    if (done) return;
    done = true;
    window.clearTimeout(timeout);
    reveal();
  };

  const timeout = window.setTimeout(finish, REVEAL_TIMEOUT_MS);

  if (video.requestVideoFrameCallback) {
    frameHandle = video.requestVideoFrameCallback(finish);
  } else {
    const poll = () => {
      if (
        !video.seeking &&
        video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
      ) {
        finish();
        return;
      }
      rafHandle = requestAnimationFrame(poll);
    };
    rafHandle = requestAnimationFrame(poll);
  }

  return () => {
    done = true;
    window.clearTimeout(timeout);
    if (frameHandle) {
      video.cancelVideoFrameCallback?.(frameHandle);
    }
    if (rafHandle) {
      cancelAnimationFrame(rafHandle);
    }
  };
};

const MutedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12.5 5.5L8 9H5v6h3l4.5 3.5v-13z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M16.2 9.8a3.4 3.4 0 010 4.4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      opacity="0.5"
    />
    <path
      d="M4.5 19.5l15-15"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const PlayIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M8 5.5v13l10.5-6.5L8 5.5z" fill="currentColor" />
  </svg>
);

interface Props {
  sources: MicroreelVideoSource[];
  fillFrame: boolean;
  onFinished?: () => void;
  onProgress?: (fraction: number) => void;
  // The parent keeps the sound choice across remounts (quiz screens).
  initialMuted?: boolean;
  onMutedChange?: (muted: boolean) => void;
}

const MicroreelVideoSurface = React.memo(
  ({
    sources,
    fillFrame,
    onFinished,
    onProgress,
    initialMuted = true,
    onMutedChange,
  }: Props) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const mutedRef = useRef(initialMuted);
    const [isMuted, setIsMuted] = useState(initialMuted);
    const [isPaused, setIsPaused] = useState(false);
    const [segmentVisible, setSegmentVisible] = useState(true);
    const [index, setIndex] = useState(0);

    // `sources` changes one render before the reset effect runs; clamping
    // keeps the element mounted through that render.
    const activeIndex = index < sources.length ? index : 0;
    const current = sources[activeIndex];
    const url = current?.url;
    const startInSeconds = current?.startInSeconds ?? 0;
    const endInSeconds = current?.endInSeconds ?? 0;

    useEffect(() => {
      setIndex(0);
    }, [sources]);

    // Read from the frame callback, which outlives the render that scheduled
    // it.
    const goToNextRef = useRef<() => void>(() => undefined);
    goToNextRef.current = () => {
      if (activeIndex < sources.length - 1) {
        setIndex(activeIndex + 1);
      } else {
        onFinished?.();
      }
    };

    const onLoadedMetadata = () => {
      const video = videoRef.current;
      if (!video || !url) return;
      startSegment(video, startInSeconds, mutedRef.current);
      setIsMuted(mutedRef.current);
      // A freshly mounted element has no previous frame to cover.
      setSegmentVisible(true);
    };

    // Segments — and consecutive reels of a group — are usually windows into
    // the same generated file. Seek inside the loaded element instead of
    // tearing it down, which is what leaves the cut continuous.
    useEffect(() => {
      const video = videoRef.current as FrameCallbackVideo | null;
      if (!video || !url) return;
      // A fresh element has no metadata yet; onLoadedMetadata starts it.
      if (video.readyState < HTMLMediaElement.HAVE_METADATA) return;
      setSegmentVisible(false);
      startSegment(video, startInSeconds, mutedRef.current);
      setIsMuted(mutedRef.current);
      return revealOnNextFrame(video, () => setSegmentVisible(true));
    }, [url, startInSeconds]);

    useEffect(() => {
      const video = videoRef.current as FrameCallbackVideo | null;
      if (!video || !url || endInSeconds <= 0) return;

      let stopped = false;
      let armed = false;
      let frameHandle = 0;
      let rafHandle = 0;
      const useFrameCallback = !!video.requestVideoFrameCallback;

      function tick(_now?: number, metadata?: {mediaTime: number}) {
        armed = false;
        if (stopped || !video) return;
        const played = metadata ? metadata.mediaTime : video.currentTime;
        if (played >= endInSeconds) {
          video.pause();
          goToNextRef.current();
          return;
        }
        schedule();
      }

      function schedule() {
        if (stopped || !video || armed) return;
        armed = true;
        if (useFrameCallback) {
          frameHandle = video.requestVideoFrameCallback!(tick);
        } else {
          rafHandle = requestAnimationFrame(() => tick());
        }
      }

      // The watcher stops at the end; a replay of the window re-arms it.
      const onPlay = () => schedule();
      video.addEventListener('play', onPlay);
      schedule();

      return () => {
        stopped = true;
        video.removeEventListener('play', onPlay);
        if (frameHandle) {
          video.cancelVideoFrameCallback?.(frameHandle);
        }
        if (rafHandle) {
          cancelAnimationFrame(rafHandle);
        }
      };
    }, [url, endInSeconds]);

    const onVolumeChange = () => {
      const video = videoRef.current;
      if (video) {
        mutedRef.current = video.muted;
        setIsMuted(video.muted);
        onMutedChange?.(video.muted);
      }
    };

    const onUnmute = () => {
      const video = videoRef.current;
      if (video) {
        video.muted = false;
      }
    };

    const togglePlayback = () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        // play() on an ended video restarts the whole file, and a paused
        // finished reel sits at its end: replay only this reel's window.
        if (
          video.ended ||
          video.currentTime < startInSeconds - SEEK_TOLERANCE_SECONDS ||
          (endInSeconds > 0 &&
            video.currentTime >= endInSeconds - SEEK_TOLERANCE_SECONDS)
        ) {
          video.currentTime = startInSeconds;
        }
        video.play().catch(() => undefined);
      } else {
        video.pause();
      }
    };

    const onTimeUpdate = () => {
      const video = videoRef.current;
      if (!onProgress || !video || !current || !sources.length) return;
      const end = endInSeconds > startInSeconds ? endInSeconds : video.duration;
      if (!end || end <= startInSeconds) return;
      const clipFraction = Math.min(
        Math.max(
          (video.currentTime - startInSeconds) / (end - startInSeconds),
          0,
        ),
        1,
      );
      onProgress((activeIndex + clipFraction) / sources.length);
    };

    if (!current) return null;

    return (
      <div className={styles.surface}>
        <video
          key={url}
          ref={videoRef}
          className={`${styles.video} ${
            fillFrame ? styles.videoCover : styles.videoContain
          } ${segmentVisible ? styles.videoVisible : styles.videoHidden}`}
          src={url}
          playsInline
          onClick={togglePlayback}
          onPlay={() => setIsPaused(false)}
          onPause={() => setIsPaused(true)}
          onLoadedMetadata={onLoadedMetadata}
          onVolumeChange={onVolumeChange}
          onTimeUpdate={onTimeUpdate}
          onEnded={() => goToNextRef.current()}
        />
        {isPaused && (
          <span className={styles.pausedBadge}>
            <PlayIcon />
          </span>
        )}
        {isMuted && (
          <button
            className={styles.unmuteButton}
            onClick={onUnmute}
            aria-label={microreelsListText.unmute}>
            <MutedIcon />
            <span>{microreelsListText.unmute}</span>
          </button>
        )}
      </div>
    );
  },
);

export default MicroreelVideoSurface;
